import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PRODUCT_FEATURE_SURFACES,
	type ProductUsageAdoptionRow,
	type ProductUsageFilters,
} from "@signalist/shared-types";

import { defaultUsageFilters } from "../../../redux/feature-usage/feature-usage.api";
import { useUsageAdoption } from "../../../redux/feature-usage/feature-usage.selectors";
import {
	flattenAreas,
	formatCount,
	formatNullable,
	formatPercent,
	formatTrend,
	sortFeatures,
} from "../utils/featureUsage";
import { MetricHelp, MetricTitle } from "./MetricHelp";
import { UsageFilters } from "./UsageFilters";
import { FeatureCell, UsageStatus, UsageSummary, UsageTable, UsageColumn } from "./UsageTable";

const sortValues = {
	feature: (row: ProductUsageAdoptionRow) => row.label,
	adoption: (row: ProductUsageAdoptionRow) => row.adoption,
	events: (row: ProductUsageAdoptionRow) => row.events,
	average: (row: ProductUsageAdoptionRow) => row.avgDailyDistinctUsers,
	trend: (row: ProductUsageAdoptionRow) =>
		row.trend.kind === "change" ? row.trend.percent : row.trend.kind === "new" ? Infinity : null,
	surfaces: (row: ProductUsageAdoptionRow) => row.surfaces.shares.web,
};
const surfaceColors = {
	web: "bg-brand-light",
	extension: "bg-status-info",
	mcp: "bg-status-success",
	api: "bg-status-warning",
};
export function AdoptionTab() {
	const { t } = useTranslation();
	const [filters, setFilters] = useState<ProductUsageFilters>(defaultUsageFilters);
	const [sort, setSort] = useState<keyof typeof sortValues>("adoption");
	const [direction, setDirection] = useState<"asc" | "desc">("desc");
	const query = useUsageAdoption(filters);
	const { data } = query;
	const cells: Omit<UsageColumn<ProductUsageAdoptionRow>, "title">[] = [
		{ key: "feature", render: (row) => <FeatureCell feature={row} /> },
		{
			key: "adoption",
			render: (row) => (
				<>
					<strong>{formatPercent(row.adoption)}</strong>
					<div className="mt-1 h-1 w-24 bg-brand-light/10">
						<div className="h-1 bg-brand-light" style={{ width: formatPercent(row.adoption) }} />
					</div>
					<div className="mt-1 text-xs text-text-meta">
						{t("featureUsage.companies", {
							used: formatCount(row.companiesUsed),
							total: formatCount(data?.cohort.size ?? 0),
						})}
					</div>
				</>
			),
		},
		{ key: "events", render: (row) => formatCount(row.events) },
		{
			key: "average",
			render: (row) => formatNullable(row.avgDailyDistinctUsers, t("featureUsage.notMeasured")),
		},
		{ key: "trend", render: (row) => formatTrend(row.trend, t("featureUsage.new")) },
		{
			key: "surfaces",
			render: (row) => (
				<div className="min-w-40">
					<div aria-hidden="true" className="mb-1 flex h-1 overflow-hidden rounded">
						{PRODUCT_FEATURE_SURFACES.map((surface) => (
							<div
								key={surface}
								className={surfaceColors[surface]}
								style={{ width: formatPercent(row.surfaces.shares[surface]) }}
							/>
						))}
					</div>
					<div className="grid grid-cols-2 gap-1 text-xs text-text-meta">
						{PRODUCT_FEATURE_SURFACES.map((surface) => (
							<span key={surface}>
								{t(`featureUsage.surfaces.${surface}`)}{" "}
								{formatPercent(row.surfaces.shares[surface])}
							</span>
						))}
					</div>
				</div>
			),
		},
	];
	const columns = cells.map<UsageColumn<ProductUsageAdoptionRow>>((column) => ({
		...column,
		title: <MetricTitle metric={column.key} />,
		direction: sort === column.key ? direction : undefined,
		sort: () => {
			const key = column.key as keyof typeof sortValues;

			setSort(key);
			setDirection(sort === key && direction === "desc" ? "asc" : "desc");
		},
	}));

	return (
		<>
			<UsageFilters plans={data?.cohort.plans ?? []} value={filters} onChange={setFilters} />
			<UsageStatus {...query} retry={query.refetch} />
			{data && !query.isError && !query.isLoading && data.lastRolledUpDate !== null && (
				<>
					<UsageSummary data={data} />
					<div className="mb-4 text-xs text-text-meta">
						{t("featureUsage.catalog", { count: data.catalogSize })}
						<MetricHelp
							label={t("featureUsage.metrics.catalog")}
							definition={t("featureUsage.help.catalog")}
						/>
					</div>
					<UsageTable
						rows={sortFeatures(flattenAreas(data.areas), sortValues[sort], direction)}
						columns={columns}
						label={t("featureUsage.tabs.adoption")}
						resetKey={`${sort}:${direction}`}
					/>
					<p className="mt-4 rounded bg-brand-light/5 p-3 text-xs text-text-meta">
						{t("featureUsage.distinctNote")}
					</p>
				</>
			)}
		</>
	);
}
